import { takeEvery, put, call, all, select } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import getAddressDetailsQuery from './getAddressDetailsQuery';
import {
  setAddressDetails,
  setAutoCompleteInputValue,
  setComponentValidationStatus,
  updateValue,
} from '../../slices/components';
import {
  handleAnswerQuestionSideEffectsSaga,
  populateFormFieldSaga,
} from '../../../../Hooks/actionCreators';

/**
 * Populates all components that have the same mappedQuestionId as the address field
 * and are not filled
 */
export function* populateMatchingAddressFields({
  components,
  mappedQuestionId,
  value,
  countryCode,
  iFrameId,
  formId,
}) {
  const matchingComponents = Object.values(components).filter(
    (component) => component.component.mappedQuestionId === mappedQuestionId
  );

  if (!matchingComponents) {
    return;
  }

  yield all(
    matchingComponents.map((component) =>
      put(
        populateFormFieldSaga({
          countryCode,
          value,
          iFrameId,
          formId,
          component,
        })
      )
    )
  );
}

/**
 * Populates all unfilled address fields with the address details obtained from Google Places API.
 */
function* populateAddressFields({
  addressDetails,
  addressMap,
  components,
  countryCode,
  formId,
  iFrameId,
}) {
  if (typeof addressMap !== 'object') {
    return;
  }

  const addressDetailNames = Object.keys(addressMap);

  yield all(
    addressDetailNames.map((addressDetailName) => {
      return populateMatchingAddressFields({
        components,
        mappedQuestionId: addressMap[addressDetailName],
        value: addressDetails[addressDetailName],
        countryCode,
        iFrameId,
        formId,
      });
    })
  );
}

const shouldComponentBePopulatedByAddressDetails = ({
  component,
  addressMap,
  addressDetails,
}) => {
  const googleAddressComponentAddressMapKey = Object.keys(addressMap).find(
    (key) => addressMap[key] === component.component.mappedQuestionId
  );
  return Boolean(addressDetails[googleAddressComponentAddressMapKey]);
};

/**
 * Maps the response of the getAddressDetails query to the address details used for populating address fields.
 * This is mostly a one to one mapping, but the stateAbbreviation is mapped to the state field for US forms.
 * In the future, if we need to support other countries, we can extend this function to map the response to the correct fields.
 */
const mapResponseToAddressDetails = (response) => {
  return {
    state: response.stateAbbreviation,
    city: response.city,
    postcode: response.postcode,
    street: response.street,
    country: response.country,
  };
};

/**
 * Handles the behaviour following the selection of an address from the google autocomplete dropdown:
 * - Obtains address details of the selected address and if required, prefills upcoming address fields
 * - Updates the input value and the value of the google autocomplete input field
 * - Sets the validation status of the google autocomplete input field
 */
export function* handleGoogleAddressSelectedWorker(action) {
  const {
    componentId,
    placeId,
    shouldPopulateUpcomingFields,
    inputValue,
    formId,
    iFrameId,
    visitorId,
  } = action.payload;
  const { formConfig, components } = yield select();
  const { countryCode } = formConfig;
  const googleAddressComponent = components[`component_${componentId}`];
  const addressMap = googleAddressComponent?.input?.addressMap;

  let newValue = inputValue;
  let isGoogleAddressFilledFromDetails = false;
  const component = components[`component_${componentId}`];
  const shouldValidatePresenceOfAddress =
    !!component?.validation?.constraints?.addressWithHouseNumber;
  let addressDetails;

  if (shouldPopulateUpcomingFields || shouldValidatePresenceOfAddress) {
    try {
      const addressDetailsResponse = yield call(getAddressDetailsQuery, {
        variables: {
          placeId,
          visitorId,
        },
      });

      addressDetails = mapResponseToAddressDetails(addressDetailsResponse);

      yield put(
        setAddressDetails({
          componentId,
          addressDetails: addressDetailsResponse,
        })
      );

      if (shouldPopulateUpcomingFields) {
        newValue = addressDetailsResponse.formattedAddress; // If we are making the request to get address details then we also obtain a more detailed formatted address and use that for the input value.

        yield populateAddressFields({
          addressDetails,
          addressMap,
          components,
          countryCode,
          formId,
          iFrameId,
        });

        isGoogleAddressFilledFromDetails =
          shouldComponentBePopulatedByAddressDetails({
            component: googleAddressComponent,
            addressMap,
            addressDetails,
          });
      }
    } catch (error) {
      // Clear the address details and fail silently
      yield put(setAddressDetails({ componentId, addressDetails: undefined }));
    }
  }

  if (!isGoogleAddressFilledFromDetails) {
    yield put(setAutoCompleteInputValue({ componentId, value: newValue }));
    yield put(updateValue({ componentId, value: newValue }));
    yield put(
      setComponentValidationStatus({
        componentId,
        iFrameId,
        formId,
      })
    );
    yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
  }
}

/**
 * Redux Watcher
 */
export default function* handleGoogleAddressSelectedWatcher() {
  yield takeEvery(
    ActionTypes.HANDLE_GOOGLE_ADDRESS_SELECTED,
    handleGoogleAddressSelectedWorker
  );
}
